import { Fragment } from 'react';
import { useFormikContext } from 'formik';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '../forms/text-field';
import CheckboxField from '../forms/checkbox-field';
import EventLocationCompact from './location-compact';
import type { EventFormValues } from './form';

type Props = {
  disabled: boolean;
  forceTouched: boolean;
};

const EventFormLocation = ({ disabled, forceTouched }: Props) => {
  const {
    values: {
      eventType,
      loop,
      startLocation,
      startLocationAddress,
      startLocationDetails,
      endLocation,
      endLocationAddress,
      endLocationDetails,
    },
  } = useFormikContext<EventFormValues>();

  if (eventType === 'ONLINE_EVENT') {
    return null;
  }

  return (
    <Fragment>
      <Typography variant="h2">Location</Typography>
      {eventType === 'RIDE' && <Typography variant="h3">Start</Typography>}
      <TextField
        name="startLocation"
        label={eventType === 'RIDE' ? 'Start place name' : 'Place name'}
        helperText='The name of the meeting place (e.g. "Gasworks Park", "Green Lake Community Center")'
        disabled={disabled}
        touched={forceTouched}
      />
      <TextField
        name="startLocationAddress"
        label={eventType === 'RIDE' ? 'Start street address (optional)' : 'Street address (optional)'}
        helperText="Street address — used for map links and calendar exports"
        disabled={disabled}
        touched={forceTouched}
      />
      <TextField
        name="startLocationDetails"
        label={eventType === 'RIDE' ? 'Start meeting instructions (optional)' : 'Meeting instructions (optional)'}
        helperText="Parking, meeting point, map links, etc. (supports markdown)"
        disabled={disabled}
        touched={forceTouched}
      />
      {(startLocation || startLocationAddress) && (
        <Box sx={{ mt: 1, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Preview
          </Typography>
          <EventLocationCompact
            name={startLocation}
            address={startLocationAddress}
            details={startLocationDetails}
            mapLinkInNewTab
          />
        </Box>
      )}
      {eventType === 'RIDE' && (
        <Fragment>
          <Typography variant="h3">End</Typography>
          <CheckboxField
            name="loop"
            label="Ride is a loop"
            helperText="If the ride start and ends at the same location"
            disabled={disabled}
            touched={forceTouched}
          />
          {!loop && (
            <Fragment>
              <TextField
                name="endLocation"
                label="End place name (optional)"
                helperText='The name of the destination (e.g. "Gasworks Park", "Green Lake Community Center")'
                disabled={disabled}
                touched={forceTouched}
              />
              <TextField
                name="endLocationAddress"
                label="End street address (optional)"
                helperText="Street address — used for map links and calendar exports"
                disabled={disabled}
                touched={forceTouched}
              />
              <TextField
                name="endLocationDetails"
                label="End meeting instructions (optional)"
                helperText="Arrival info, parking, map links, etc. (supports markdown)"
                disabled={disabled}
                touched={forceTouched}
              />
              {(endLocation || endLocationAddress) && (
                <Box sx={{ mt: 1, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Preview
                  </Typography>
                  <EventLocationCompact
                    name={endLocation}
                    address={endLocationAddress}
                    details={endLocationDetails}
                    mapLinkInNewTab
                  />
                </Box>
              )}
            </Fragment>
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

export default EventFormLocation;
